// A switch is the only button that is a field's control.
const SWITCH = 'button[role="switch"]';
// A select's trigger is a span, so `for` cannot reach it and aria-labelledby
// is the whole name.
const COMBOBOX = '[role="combobox"]';
const LABELABLE = `input:not([type="hidden"]), select, textarea, ${SWITCH}, ${COMBOBOX}`;
const ERRORS = '[data-form-error]';
const HELP = '.form-group__help';
const LABEL = '[data-field-label]';
const LABEL_TEXT = '[data-field-label-text]';
const REQUIRED = 'data-field-required';
const REQUIRED_MARKER = '[data-field-required-marker]';

const INSIDE_A_MENU = '[role="listbox"], [role="menu"]';

const TRIGGER = '[aria-haspopup="listbox"]';
const NAMED = 'data-field-named';

let fieldIds = 0;

const MANAGED_DESCRIPTION = /^elm-field-\d+-description-\d+$/;
const MANAGED_LABEL = /^elm-field-\d+-label$/;

function describe(control, descriptionIds) {
  const ids = [
    ...new Set(
      (control.getAttribute('aria-describedby') ?? '')
        .split(' ')
        .filter((id) => id && !MANAGED_DESCRIPTION.test(id))
        .concat(descriptionIds),
    ),
  ];

  if (ids.length === 0) control.removeAttribute('aria-describedby');
  else control.setAttribute('aria-describedby', ids.join(' '));
}

function unname(control) {
  const name = control.getAttribute('aria-labelledby') ?? '';
  if (control.hasAttribute(NAMED) || MANAGED_LABEL.test(name)) {
    control.removeAttribute('aria-labelledby');
    control.removeAttribute(NAMED);
  }
}

class ElmField extends HTMLElement {
  connectedCallback() {
    this.fieldId ??= `elm-field-${++fieldIds}`;
    this.wire();
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  own(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => el.closest('elm-field') === this);
  }

  describedBy(node, controls) {
    for (let el = node.parentElement; el; el = el.parentElement) {
      const within = controls.filter((control) => el.contains(control));
      if (within.length === 1) return within;
      if (within.length > 1) return controls;
      if (el === this) break;
    }
    return controls;
  }

  wire() {
    const labelable = this.own(LABELABLE).filter((el) => !el.closest(INSIDE_A_MENU));
    const triggers = this.own(TRIGGER);
    const trigger = labelable.length === 0 && triggers.length === 1 ? triggers[0] : null;
    const controls = trigger ? [trigger] : labelable;
    const described = new Map(controls.map((control) => [control, []]));

    this.own(`${ERRORS}, ${HELP}`).forEach((node, index) => {
      if (!node.id || MANAGED_DESCRIPTION.test(node.id)) {
        node.id = `${this.fieldId}-description-${index}`;
      }
      this.describedBy(node, controls).forEach((control) => described.get(control).push(node.id));
    });

    controls.forEach((control) => describe(control, described.get(control)));

    // On a group, aria-required would mark its optional parts required too.
    const marker = this.own(REQUIRED_MARKER)[0];
    const onControl = this.hasAttribute(REQUIRED) && labelable.length === 1;

    labelable.forEach((control) => control.removeAttribute('aria-required'));
    if (onControl) {
      labelable[0].setAttribute('aria-required', 'true');
      marker?.setAttribute('aria-hidden', 'true');
    } else {
      marker?.removeAttribute('aria-hidden');
    }

    const [label] = this.own(LABEL);
    if (!label) {
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      [...labelable, ...triggers].forEach(unname);
      return;
    }

    const [labelText] = this.own(LABEL_TEXT);
    const nameSource = labelText ?? label;
    nameSource.id ||= `${this.fieldId}-label`;
    const names = [nameSource];
    if (marker && !onControl) {
      marker.id ||= `${this.fieldId}-required`;
      names.push(marker);
    }

    if (trigger) {
      label.removeAttribute('for');
      trigger.id ||= `${this.fieldId}-control`;
      const ids = [...names, trigger].map((node) => node.id);
      trigger.setAttribute('aria-labelledby', ids.join(' '));
      trigger.setAttribute(NAMED, '');
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      return;
    }

    triggers.forEach(unname);
    triggers.filter((t) => !controls.includes(t)).forEach((t) => describe(t, []));

    const [control] = controls;
    if (controls.length === 1 && !control.closest('label')) {
      control.id ||= `${this.fieldId}-control`;
      label.htmlFor = control.id;
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      // A switch's own name carries why it is disabled.
      if (labelText && !control.matches(SWITCH)) {
        control.setAttribute('aria-labelledby', labelText.id);
      } else {
        unname(control);
      }
    } else {
      label.removeAttribute('for');
      this.setAttribute('role', 'group');
      this.setAttribute('aria-labelledby', names.map((node) => node.id).join(' '));
      controls.forEach(unname);
    }
  }
}

if (!customElements.get('elm-field')) {
  customElements.define('elm-field', ElmField);
}
